import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import { PageContainer } from 'shared/components/PageContainer';
import { RentalPageTitleHelmet } from 'rental/components/PageTitleHelmet';
import { NextButton, PrevLink, SubmitButtons } from 'shared/components/SubmitButtons';
import { PageHeading } from 'shared/components/typography';
import { App_ApplicationFragment, App_PortalFragment, usePortalCreditGuideQuery, useSignatureQuery, useSubmitApplicationMutation } from '../../generated.graphql';
import { RentalStepProps } from 'rental/app/steps';
import { DigitalSignature } from './DigitalSignature';
import { SmsSignature } from './SmsSignature';
import { yupRequiredMsg } from '../../../shared/yup';
import { usePortalAnalytics } from '../../hooks/analytics';

interface Props extends RentalStepProps {
  portal: App_PortalFragment;
  application: App_ApplicationFragment;
}

interface Values {
  signature: string;
}

export const Signature = ({ portal, application, ...props }: Props) => {
  const [portalResult] = usePortalCreditGuideQuery({ variables: { portalSlug: portal.slug }});
  const [signatureResult] = useSignatureQuery({ variables: { portalSlug: portal.slug }});
  const [mutationResult, submitApplicationMutation] = useSubmitApplicationMutation();
  const [showSmsSignature, changeShowSmsSignature] = useState(application.remainingSmsSignatureCount > 0);
  const { updateEnhancedConversionData } = usePortalAnalytics(portal);

  const errors = mutationResult.data?.submitApplication?.errors ?? [];
  const signature = application.digitalSignature.trim().toLocaleUpperCase();
  const creditGuideUrl = portalResult.data?.portal?.policies?.creditGuideUrl;
  const customer = signatureResult.data?.portal?.myApplication?.customer;
  const address = signatureResult.data?.portal?.myApplication?.address?.residentialAddress;

  // since this is the very last step before submission, ensure the customer details are updated
  // so the google ads enhanced conversion data is correct, ready for checkout completion
  useEffect(() => {
    if (customer) {
      updateEnhancedConversionData(customer, address);
    }
  }, [ updateEnhancedConversionData, customer, address ]);

  return (
    <PageContainer>
      <RentalPageTitleHelmet>Submit Application</RentalPageTitleHelmet>
      <PageHeading>Application Summary</PageHeading>
      <p>This is the last step of your application process. {creditGuideUrl && <>Please take a moment to read our Credit Guide before finalising your application.</>}</p>
      {creditGuideUrl ? <p>See the <a href={creditGuideUrl} target="_blank" rel="noreferrer">Polygon Credit Guide</a></p> : <p>&nbsp;</p>}
      
      <Formik<Values>
        initialValues={{
          signature: application.digitalSignatureRequired ? '' : signature,
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={yup.object().shape({
          signature: yup.string()
            .transform((value, original) => {
              return value?.trim()?.toLocaleUpperCase();
            })
            .matches(new RegExp(`^${signature}$`), 'Signature does not match')
            .required(yupRequiredMsg)
        })}
        onSubmit={async (data, helpers) => {
          const r = await submitApplicationMutation({
            input: {
              portalSlug: application.portal.slug,
              signature: data.signature,
            },
          });
          if (r.data?.submitApplication?.application?.dateSubmitted) {
            props.onSubmitSuccessful();
          } else {
            props.onSubmitFailure();
          }
        }}
      >
        {(formik) => (
          <>
            {application.digitalSignatureRequired && (
              <>
                <PageHeading className="mt-5">Digital Signature</PageHeading>
                {showSmsSignature ? 
                  <SmsSignature
                    portal={portal}
                    application={application}
                    canSubmit={!!formik.values.signature /*formik.values.signature === signature && !mutationResult.fetching*/}
                    onSubmit={() => formik.submitForm()}
                    onFallback={() => changeShowSmsSignature(false)}
                  />
                  :
                  <DigitalSignature application={application} />
                }
              </>
            )}

            {errors.length > 0 && (
              <div className="alert alert-danger">
                <p>There were errors when trying to submit the application:</p>
                <ul className="mb-0">
                  {errors.map((error, i) => (
                    <li key={i}>
                      <b>{error?.field}:</b> {error?.messages}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(!showSmsSignature || !application.digitalSignatureRequired) && (
              <SubmitButtons
                left={props.steps?.prev && <PrevLink to={props.steps.prev.link} />}
                right={
                  <NextButton 
                    onClick={() => formik.submitForm()}
                    disabled={formik.values.signature !== signature || mutationResult.fetching}
                  >
                    Submit
                  </NextButton>
                }
              />
            )}
          </>
        )}
      </Formik>

    </PageContainer>
  );
}